/**
 * Resolving the team behind a case's assignee (BV-2228).
 *
 * The assignee's team is the one thing about them the case surfaces never show:
 * it appears only as a group heading inside the assignee dropdown, which is gone
 * the moment the cell renders read-only. Both surfaces already hold the team
 * names they need — the dropdown is built from them — so this is a lookup, not a
 * fetch.
 */

/** A team as the assignee pickers know it: an id and a display name. */
export interface AssigneeTeamOption {
  id: string;
  name: string;
}

/**
 * Finds the display name for `teamId`, or undefined when it resolves to nothing.
 *
 * The teams passed in are program- and workflow-type-scoped, so a case assigned
 * through a team outside that scope resolves to nothing. That degrades to no
 * team in the tooltip rather than guessing one — naming the wrong team is worse
 * than naming none.
 */
export const resolveAssigneeTeamName = (
  teamId: string | undefined,
  teams: readonly AssigneeTeamOption[],
): string | undefined => {
  if (!teamId) return undefined;
  return teams.find(team => team.id === teamId)?.name || undefined;
};

/**
 * Composes the assignee tooltip.
 *
 * Without a team this is just the assignee's own label — which is what the
 * tooltip showed before, duplicating the text already on screen. The point of
 * the tooltip is the team; the name is only there for context.
 */
export const formatAssigneeTooltip = (
  assigneeLabel: string | undefined,
  teamName: string | undefined,
): string | undefined => {
  if (!assigneeLabel) return undefined;
  return teamName ? `${assigneeLabel} — ${teamName}` : assigneeLabel;
};
